import React from "react";
import { useFragment } from "@apollo/client/react";

import { MigrateIssueTypeDialog } from "#app/components/issue-view/migrate-issue-type-dialog";
import { useIssueTypesWithFields } from "#app/hooks/issue-view/use-issue-types-with-fields";
import { useMigrateIssueType } from "#app/hooks/issue-view/use-migrate-issue-type";
import { IssueSummaryFragmentDoc } from "#app/lib/data";
import { createDialogEmitter } from "#app/lib/dialog-emitter";

type TypeMigrationRequest = {
  issueId: string;
  issueTypeId: string;
  /** Runs after the server accepts the change. */
  onMigrated?: (() => void) | undefined;
  /** The surface, for `kanban.change_type`: "card" or "menu". */
  via: string;
};

const typeMigrationDialog = createDialogEmitter<TypeMigrationRequest>("issue_type_migration");

/** Change a project's type through the migrate dialog — the confirmation and
 *  required-field step every type change goes through. */
export function useOpenIssueTypeMigration(): (request: TypeMigrationRequest) => void {
  return typeMigrationDialog.open;
}

export function IssueTypeMigrationDialogMount() {
  const { open, payload, setOpen } = typeMigrationDialog.useDialogState();
  const issueTypes = useIssueTypesWithFields();
  const migrateIssueType = useMigrateIssueType();
  const { data: issue } = useFragment({
    fragment: IssueSummaryFragmentDoc,
    fragmentName: "IssueSummary",
    from: { __typename: "Issue", issueId: payload?.issueId ?? "" },
  });

  if (!open || !payload || payload.issueTypeId === issue.issueTypeId) return null;
  return (
    <MigrateIssueTypeDialog
      open
      onOpenChange={setOpen}
      issueTypes={issueTypes}
      issueTypeId={payload.issueTypeId}
      values={(issue.values as Record<string, unknown> | null | undefined) ?? {}}
      issueName={issue.name ?? ""}
      onConfirm={async (issueTypeId, values) => {
        if (await migrateIssueType(payload.issueId, issueTypeId, values, payload.via)) {
          payload.onMigrated?.();
        }
        setOpen(false);
      }}
    />
  );
}
